// Clerk Permission Mapper
// Maps Clerk feature:permission format to navigation and action permissions

import { PERMISSION_CONSTANTS } from '@/constants/permissions';

// Map action keys to Clerk permissions
export const ACTION_TO_PERMISSION: Record<string, string> = {
    // Workflow actions
    'workflow.create': 'automation_management:all',
    'workflow.read': 'automation_read:all',
    'workflow.update': 'automation_management:all',
    'workflow.delete': 'automation_management:all',

    // Contact actions
    'contact.create': 'contacts_management:all',
    'contact.read': 'contacts_read:all',
    'contact.update': 'contacts_management:all',
    'contact.delete': 'contacts_management:all',
    'contact.archive': 'contacts_management:all',
    'contact.import': 'contacts_management:all',
    'contact.export': 'contacts_management:all',

    // User actions
    'user.create': 'settings_management:all',
    'user.read': 'settings_read:all',
    'user.update': 'settings_management:all',
    'user.delete': 'settings_management:all',
    'user.invite': 'settings_management:all',

    // Team actions
    'team.create': 'settings_management:all',
    'team.read': 'settings_read:all',
    'team.update': 'settings_management:all',
    'team.delete': 'settings_management:all',

    // Tag actions
    'tag.create': 'settings_management:all',
    'tag.read': 'settings_read:all',
    'tag.update': 'settings_management:all',
    'tag.delete': 'settings_management:all',
    'tag.archive': 'settings_management:all',

    // Topic actions
    'topic.create': 'settings_management:all',
    'topic.read': 'settings_read:all',
    'topic.update': 'settings_management:all',
    'topic.delete': 'settings_management:all',
    'topic.archive': 'settings_management:all',

    // Chatbot actions
    'chatbot.create': 'automation_management:all',
    'chatbot.read': 'automation_read:all',
    'chatbot.update': 'automation_management:all',
    'chatbot.delete': 'automation_management:all',
    'chatbot.manage': 'automation_management:all',

    // Report actions
    'report.create': 'reporting_management:all',
    'report.read': 'reporting_read:all',
    'report.update': 'reporting_management:all',
    'report.delete': 'reporting_management:all',

    // Workspace actions
    'workspace.create': 'workspace_management:all',
    'workspace.read': 'workspace_read:all',
    'workspace.update': 'workspace_management:all',
    'workspace.manage': 'workspace_management:all',

    // Profile actions
    'profile.read': 'settings_read:all',
    'profile.update': 'settings_management:all',

    // Settings actions
    'settings.teammates': 'settings_management:all',
    'settings.teams': 'settings_management:all',
    'settings.workspace': 'workspace_management:all',

    // Domain management
    'domain.create': 'settings_management:all',
    'domain.read': 'settings_read:all',
    'domain.update': 'settings_management:all',
    'domain.delete': 'settings_management:all',
};

// Map navigation keys to Clerk permissions
export const NAVIGATION_TO_PERMISSION: Record<string, string> = {
    // Main navigation
    'home': 'workspace_read:all',
    'inbox': 'inbox_read:all',
    'contacts': 'contacts_read:all',
    'automation': 'automation_read:all',
    'reporting': 'reporting_read:all',
    'settings': 'settings_read:all',

    // Inbox sub-navigation
    'inbox.your-inbox': 'inbox_read:your',
    'inbox.mentions': 'inbox_read:mentions',
    'inbox.all': 'inbox_read:alltickets',
    'inbox.unassigned': 'inbox_read:unassigned',
    'inbox.teams': 'inbox_read:teams',
    'inbox.teammates': 'inbox_read:teammates',
    'inbox.bot': 'inbox_read:bot',
    'inbox.bot/:botId': 'inbox_read:bot',

    // Contacts sub-navigation
    'contacts.all': 'contacts_read:all',
    'contacts.visitors': 'contacts_read:all',
    'contacts.customers': 'contacts_read:all',
    'contacts.companies': 'contacts_read:all',

    // Automation sub-navigation
    'automation.content-center': 'automation_read:contentcenter',
    'automation.chatbot-profiles': 'automation_read:chatbotprofiles',
    'automation.action-center': 'automation_read:actioncenter',
    'automation.prebuilt-actions': 'automation_read:prebuiltactions',
    'automation.bot-inboxes': 'automation_read:botinboxes',
    'automation.copilot': 'automation_read:copilot',
    'automation.workflows': 'automation_read:workflows',
    'automation.workflow-templates': 'automation_read:workflowtemplates',
    'automation.tools': 'automation_read:tools',

    // Reporting sub-navigation
    'reporting.dashboard': 'reporting_read:all',
    'reporting.reports': 'reporting_read:all',
    'reporting.custom': 'reporting_read:all',

    // Settings sub-navigation
    'settings.general': 'settings_read:general',
    'settings.tickets': 'settings_read:tickets',
    'settings.teammates': 'settings_read:teammates',
    'settings.teams': 'settings_read:teams',
    'settings.chat': 'settings_read:chat',
    'settings.email': 'settings_read:email',
    'settings.custom-objects': 'settings_read:customobjects',
    'settings.custom-data': 'settings_read:customdata',
    'settings.tags': 'settings_read:tags',
    'settings.topics': 'settings_read:topics',
    'settings.canned-responses': 'settings_read:cannedresponses',
    'settings.sentiment': 'settings_read:sentiment',
    'settings.autoqa': 'settings_read:autoqa',
    'settings.auto-reply': 'settings_read:autoreply',
    'settings.email-templates': 'settings_read:emailtemplates',
};

/**
 * Get Clerk permission for an action
 */
export function getPermissionForAction(action: string): string {
    return ACTION_TO_PERMISSION[action] || 'workspace_read:all';
}

/**
 * Get Clerk permission for navigation
 */
export function getPermissionForNavigation(navigationKey: string): string {
    return NAVIGATION_TO_PERMISSION[navigationKey] || 'workspace_read:all';
}

/**
 * Check if a permission is a management permission
 */
export function isManagementPermission(permission: string): boolean {
    return permission.includes('_management:');
}

/**
 * Check if a permission is a read permission
 */
export function isReadPermission(permission: string): boolean {
    return permission.includes('_read:');
} 